// anUi.js — briques d'interface partagées de l'Analyseur (classes an-*).
// Mêmes conventions visuelles que ScanTab (badge dégradé, barre win rate, ▲/▼, point d'activité).

export const JOURS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

export const shortAddr = (a) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a || '');

export function fmtUsd(n, signed = false) {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : (signed && n > 0 ? '+' : '');
  const abs = Math.abs(n);
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  if (abs > 0 && abs < 1) return `${sign}$${abs.toFixed(2)}`;
  return `${sign}$${abs.toFixed(0)}`;
}

// "2h15" / "45min" / "3j"
export function fmtHold(min) {
  if (min == null) return '—';
  if (min < 1) return '< 1min';
  if (min < 60) return `${min}min`;
  if (min < 1440) {
    const h = Math.floor(min / 60), r = min % 60;
    return r > 0 ? `${h}h${String(r).padStart(2, '0')}` : `${h}h`;
  }
  return `${Math.round(min / 1440)}j`;
}

export const fmtDay = (ts) => (ts ? new Date(ts * 1000).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');
export const fmtTime = (ts) => (ts ? new Date(ts * 1000).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—');
export const fmtDateTime = (ms) => new Date(ms).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
export const heureLabel = (h) => (h == null ? '—' : `${h}h-${h + 1}h`);

export function scoreTier(score) {
  if (score == null) return { cls: 'an-score--none', label: '' };
  if (score > 70) return { cls: 'an-score--excellent', label: 'Excellent' };
  if (score >= 50) return { cls: 'an-score--good', label: 'Bon' };
  if (score >= 30) return { cls: 'an-score--medium', label: 'Moyen' };
  return { cls: 'an-score--weak', label: 'Faible' };
}

export const verdictLabel = (reco) => (reco === 'Ne pas suivre' ? 'À éviter' : reco);
export const verdictCls = (reco) => (reco === 'Suivre' ? 'an-verdict--follow' : reco === 'Ne pas suivre' ? 'an-verdict--avoid' : 'an-verdict--watch');

export function ScoreBadge({ score }) {
  const t = scoreTier(score);
  return (
    <span className={`an-score ${t.cls}`}>
      {score ?? '—'}{t.label && <span className="an-score-label">{t.label}</span>}
    </span>
  );
}

export function WinBar({ value }) {
  if (value == null) return <span className="an-muted">—</span>;
  const tone = value > 60 ? 'good' : value >= 40 ? 'mid' : 'bad';
  return (
    <div className="an-wr">
      <div className={`an-wr-fill an-wr-fill--${tone}`} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
      <span className="an-wr-value">{Math.round(value)}%</span>
    </div>
  );
}

export function Pnl({ value, digits = 1 }) {
  if (value == null || Number.isNaN(value)) return <span className="an-pnl an-pnl--zero">—</span>;
  const txt = Math.abs(value).toLocaleString('fr-FR', { minimumFractionDigits: digits, maximumFractionDigits: digits });
  if (value > 0) return <span className="an-pnl an-pnl--up">▲ {txt}%</span>;
  if (value < 0) return <span className="an-pnl an-pnl--down">▼ {txt}%</span>;
  return <span className="an-pnl an-pnl--zero">0%</span>;
}

export function Money({ value, signed = true }) {
  if (value == null || Number.isNaN(value)) return <span className="an-pnl an-pnl--zero">—</span>;
  const cls = value > 0 ? 'an-pnl--up' : value < 0 ? 'an-pnl--down' : 'an-pnl--zero';
  return <span className={`an-pnl ${cls}`}>{fmtUsd(value, signed)}</span>;
}

// ts en secondes
export function Activity({ ts }) {
  if (!ts) return <span className="an-act"><span className="an-dot an-dot--old" />—</span>;
  const h = (Date.now() / 1000 - ts) / 3600;
  const ago = fmtDateTime(ts * 1000);
  if (h < 24) return <span className="an-act" title={ago}><span className="an-dot an-dot--live" />Actif</span>;
  if (h < 72) return <span className="an-act" title={ago}><span className="an-dot an-dot--recent" />Récent</span>;
  return <span className="an-act" title={ago}><span className="an-dot an-dot--old" />{new Date(ts * 1000).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>;
}

export function ResultBadge({ resultat }) {
  const cls = resultat === 'Gagnant' ? 'an-result--win' : resultat === 'Perdant' ? 'an-result--loss' : 'an-result--open';
  return <span className={`an-result ${cls}`}>{resultat}</span>;
}

export function InfoTip({ text }) { return <span className="an-tip" data-tip={text}>?</span>; }

export function Kpi({ label, value, tone = '', tip }) {
  return (
    <div className="an-kpi">
      <div className="an-kpi-label">{label}{tip && <InfoTip text={tip} />}</div>
      <div className={`an-kpi-value${tone ? ` an-kpi-value--${tone}` : ''}`}>{value}</div>
    </div>
  );
}

export function Skel({ w = '100%', h = 14 }) { return <div className="an-skel" style={{ width: w, height: h }} />; }

export function Section({ icon, title, right, children }) {
  return (
    <div className="an-section">
      <div className="an-section-head">
        <div className="an-section-title"><span className="an-section-icon">{icon}</span>{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

export function TokenCell({ p }) {
  return (
    <div className="an-token">
      {p.image
        ? <img src={p.image} alt={p.symbol} className="an-token-avatar" onError={e => { e.target.style.display = 'none'; }} />
        : <div className="an-token-avatar an-token-avatar--ph">{(p.symbol || '?')[0]}</div>}
      <div><div className="an-token-sym">{p.symbol}</div><div className="an-token-name">{p.name}</div></div>
    </div>
  );
}

export const openToken = (mint) => { if (mint) window.open(`https://solscan.io/token/${mint}`, '_blank', 'noopener,noreferrer'); };

export function ExtLink({ mint }) {
  return <a className="an-ext" href={`https://solscan.io/token/${mint}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} title="Ouvrir dans Solscan">↗</a>;
}

export function Pager({ page, total, onPage }) {
  if (total <= 1) return null;
  const nums = Array.from({ length: total }, (_, i) => i + 1)
    .filter(n => n === 1 || n === total || Math.abs(n - page) <= 1)
    .reduce((acc, n, i, arr) => { if (i > 0 && n - arr[i - 1] > 1) acc.push('…'); acc.push(n); return acc; }, []);
  return (
    <div className="an-pager">
      <button className="an-page-btn" disabled={page === 1} onClick={() => onPage(Math.max(1, page - 1))}>‹</button>
      {nums.map((n, i) => n === '…'
        ? <span key={`e${i}`} className="an-muted">…</span>
        : <button key={n} className={`an-page-btn${page === n ? ' an-page-btn--active' : ''}`} onClick={() => onPage(n)}>{n}</button>)}
      <button className="an-page-btn" disabled={page === total} onClick={() => onPage(Math.min(total, page + 1))}>›</button>
    </div>
  );
}

// Courbe SVG simple (PNL cumulé, capital simulé). baseline = ligne de référence pointillée.
export function LineChart({ values, baseline = 0, height = 100, width = 600 }) {
  if (!values || values.length < 2) return null;
  const PAD = 10;
  const min = Math.min(...values, baseline), max = Math.max(...values, baseline);
  const range = max - min || 1;
  const x = (i) => PAD + (i / (values.length - 1)) * (width - PAD * 2);
  const y = (v) => PAD + (1 - (v - min) / range) * (height - PAD * 2);
  const line = values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
  const area = `${line}L${x(values.length - 1).toFixed(1)},${height - PAD}L${PAD},${height - PAD}Z`;
  const last = values[values.length - 1];
  const up = last >= baseline;
  const col = up ? '#22c55e' : '#ef4444';
  const by = y(baseline);
  return (
    <svg className="an-chart" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {by > PAD && by < height - PAD && <line x1={PAD} y1={by} x2={width - PAD} y2={by} stroke="#1a2030" strokeWidth="1" strokeDasharray="4,4" />}
      <path d={area} fill={up ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)'} />
      <path d={line} fill="none" stroke={col} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={x(values.length - 1)} cy={y(last)} r="3" fill={col} />
    </svg>
  );
}
